/**
 * TermsGate — global mount that polls `/api/legal/my-acceptance` when a
 * logged-in user lands anywhere in the app and pops the acceptance modal
 * if their Terms of Use version is stale (or never accepted).
 *
 * This exists so we can bump the ToS version on the backend and every
 * active member is force-prompted to re-accept before continuing.
 */

import React, { useContext, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { AuthContext } from '../App';
import TermsAcceptanceModal from './TermsAcceptanceModal';
import axios from 'axios';

// Routes where the acceptance modal MUST NOT appear — otherwise users
// have no way to read the Terms of Use before deciding to accept them,
// and the public leak-trace resolver would get blocked for admins.
const BYPASS_PATH_PREFIXES = ['/terms', '/leak/', '/community-standards', '/help', '/team/join/'];

const TermsGate = () => {
  const { isAuthenticated, API } = useContext(AuthContext);
  const location = useLocation();
  const [needsAcceptance, setNeedsAcceptance] = useState(false);
  const [currentVersion, setCurrentVersion] = useState('');

  useEffect(() => {
    if (!isAuthenticated || !API) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/legal/my-acceptance`);
        if (cancelled) return;
        setCurrentVersion(data.current_version || '');
        setNeedsAcceptance(!data.up_to_date);
      } catch (_) {
        /* silent — never block the app on this */
      }
    })();
    return () => { cancelled = true; };
  }, [isAuthenticated, API]);

  // Suppress the modal when the user is on a page that helps them
  // decide whether to accept (Terms of Use, Community Standards, Help).
  const onBypassPath = BYPASS_PATH_PREFIXES.some((p) => location.pathname.startsWith(p));

  return (
    <TermsAcceptanceModal
      open={needsAcceptance && !onBypassPath}
      currentVersion={currentVersion}
      onAccepted={() => setNeedsAcceptance(false)}
    />
  );
};

export default TermsGate;
